import React, { useEffect, useState, useCallback } from "react";
import PublicLayout from "@/components/public/PublicLayout";
import PartCard from "@/components/public/PartCard";
import { publicApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { partCategoryLabel, PART_CONDITIONS } from "@/lib/format";
import { Search, X, Wrench } from "lucide-react";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

const DEFAULTS = {
  q: "", part_category: "all", brand: "all", compatible_reference: "",
  condition: "all", origin: "all", availability: "all", sort: "newest",
};

export default function Parts() {
  const [filters, setFilters] = useState(DEFAULTS);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    publicApi.get("/public/parts/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    publicApi.get("/public/parts/brands").then((r) => setBrands(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.part_category !== "all") params.part_category = filters.part_category;
    if (filters.brand !== "all") params.brand = filters.brand;
    if (filters.compatible_reference) params.compatible_reference = filters.compatible_reference;
    if (filters.condition !== "all") params.condition = filters.condition;
    if (filters.origin !== "all") params.origin = filters.origin;
    if (filters.availability !== "all") params.availability = filters.availability;
    if (filters.sort) params.sort = filters.sort;
    publicApi.get("/public/parts", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce typing
    return () => clearTimeout(t);
  }, [load]);

  const activeFilters = JSON.stringify(filters) !== JSON.stringify(DEFAULTS);

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-[#c9a86a] text-xs uppercase tracking-[0.2em] mb-2"><Wrench size={14} /> Watch Parts</div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[var(--text)]" style={{ fontFamily: "'Playfair Display', serif" }}>Parts &amp; Components</h1>
          <p className="text-[var(--text-2)] mt-2 max-w-2xl">Genuine and aftermarket watch parts from the LCH inventory. Search by reference or part number, or inquire for anything you don't see.</p>
        </div>

        {/* Filter bar */}
        <div className="bg-[var(--panel-2)] border border-[var(--border-2)] p-4 mb-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Search</Label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-2)]" />
              <Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Title, brand, model, ref, part #" className="pl-9 bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-search" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Part Type</Label>
            <Select value={filters.part_category} onValueChange={(v) => set("part_category", v)}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{partCategoryLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Brand</Label>
            <Select value={filters.brand} onValueChange={(v) => set("brand", v)}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-filter-brand"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Compatible Reference</Label>
            <Input value={filters.compatible_reference} onChange={(e) => set("compatible_reference", e.target.value)} placeholder="e.g. 16610" className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-filter-compat" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Condition</Label>
            <Select value={filters.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-filter-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Condition</SelectItem>
                {PART_CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Origin</Label>
            <Select value={filters.origin} onValueChange={(v) => set("origin", v)}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-filter-origin"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">OEM &amp; Aftermarket</SelectItem>
                <SelectItem value="oem">OEM / Factory</SelectItem>
                <SelectItem value="aftermarket">Aftermarket</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Availability</Label>
            <Select value={filters.availability} onValueChange={(v) => set("availability", v)}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-filter-availability"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="in_stock">In Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Sort</Label>
            <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)]" data-testid="parts-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {activeFilters && (
            <div className="lg:col-span-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setFilters(DEFAULTS)} data-testid="parts-clear-filters" className="text-[var(--text-2)]"><X size={14} className="mr-1" /> Clear filters</Button>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="parts-loading">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[var(--panel-2)] border border-[var(--border-2)] animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[var(--border-2)]" data-testid="parts-empty">
            <Wrench size={40} className="mx-auto text-[var(--border-2)] mb-3" />
            <p className="text-[var(--text)] font-medium">No parts match your search</p>
            <p className="text-[var(--text-2)] text-sm mt-1">Try clearing filters — or use the request form on any listing to source a specific part.</p>
          </div>
        ) : (
          <>
            <div className="text-xs text-[var(--text-2)] mb-3" data-testid="parts-count">{items.length} part{items.length === 1 ? "" : "s"}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="parts-grid">
              {items.map((p) => <PartCard key={p.id} part={p} />)}
            </div>
          </>
        )}
      </section>
    </PublicLayout>
  );
}
